import { currentYear } from "@/lib/siteYear";
// Config for Amazon-powered buyer's guides (one per product category)
// These are used by the generic AmazonBuyersGuide template at /best-{slug}-uk

export interface BuyersGuideConfig {
  slug: string;            // URL slug, e.g. "hedge-trimmers" -> /best-hedge-trimmers-uk
  title: string;           // H1 / display title (plural product noun)
  singular: string;        // singular noun for prose, e.g. "hedge trimmer"
  amazonKeywords: string;  // search query sent to PA-API
  searchIndex?: string;    // PA-API SearchIndex (default "All")
  intro: string;           // paragraph below H1
  considerations: { title: string; body: string }[]; // buyer's-guide bullets
  faqs?: { q: string; a: string }[];
  heroGradient: string;
  productCategorySlug?: string; // links back to /products/{slug} landing
  sections?: BuyersGuideSection[]; // optional split into product types, each with its own Amazon query
}

export interface BuyersGuideSection {
  id: string;
  title: string;
  intro: string;
  amazonKeywords: string;
  mustMatch: string;   // case-insensitive regex the product title must contain
  exclude?: string;    // case-insensitive regex that rejects a product title
  pinnedAsins?: string[]; // hand-picked Amazon products always shown in this section
  pinnedOnly?: boolean;   // show only the pinned products (no live Amazon search)
  reviews?: Record<string, string>; // ASIN -> article slug for "Read our review" (shown once published)
}

const Y = Math.max(currentYear(), 2026);

export const buyersGuides: Record<string, BuyersGuideConfig> = {
  "hedge-trimmers": {
    slug: "hedge-trimmers",
    title: "Hedge Trimmers",
    singular: "hedge trimmer",
    amazonKeywords: "hedge trimmer cordless petrol electric UK",
    intro:
      "A good hedge trimmer turns a dreaded chore into a quick afternoon job. Whether you have a low box hedge or a tall run of leylandii, the right model makes neat, safe work easy. We've gathered the highest-rated UK hedge trimmers on Amazon, refreshed automatically.",
    considerations: [
      { title: "Power source", body: "Cordless trimmers are the most popular for UK gardens — light, quiet and quick to start. Corded models are cheaper and never run flat but limit your reach. Petrol is best reserved for very long or thick hedges." },
      { title: "Blade length", body: "Short blades (40–45cm) suit small ornamental hedges. 50–55cm is the sweet spot for most gardens. 60cm+ blades cover long runs faster but are heavier to hold." },
      { title: "Cutting capacity", body: "Look at the tooth gap (mm). 18–22mm handles most garden hedges; 24mm+ is needed for thick, woody growth like overgrown privet or laurel." },
      { title: "Weight & balance", body: "You'll be holding it overhead, so anything under 4kg is comfortable for long sessions. Check for an anti-vibration handle and rotating rear grip for vertical cuts." },
    ],
    faqs: [
      { q: "Cordless or petrol hedge trimmer?", a: "For 95% of UK gardens, a 36V or 40V cordless trimmer offers more than enough power, with no fuel mixing or pull-starts." },
      { q: "What's a good blade length?", a: "50cm is the all-rounder. Go shorter for fiddly topiary, longer for large hedge runs." },
    ],
    heroGradient: "bg-linear-to-br from-green-50 to-teal-50",
    productCategorySlug: "hedge-trimmers",
  },
  "scarifiers": {
    slug: "scarifiers",
    title: "Lawn Scarifiers",
    singular: "scarifier",
    amazonKeywords: "lawn scarifier electric petrol UK",
    intro: "A scarifier rakes out moss and thatch to let your lawn breathe — the single biggest improvement you can make to a tired UK lawn. Here are the highest-rated scarifiers on Amazon.",
    considerations: [
      { title: "Working width", body: "30–35cm suits small lawns; 38cm+ is faster on medium-to-large gardens." },
      { title: "Power", body: "Electric (1500W+) handles most domestic lawns. Petrol is better for very large or wet lawns." },
      { title: "Adjustable depth", body: "Look for at least 3 working depths so you can lightly aerate or aggressively dethatch." },
      { title: "Collection box", body: "A built-in collector saves a lot of raking afterwards." },
    ],
    heroGradient: "bg-linear-to-br from-lime-50 to-green-50",
    productCategorySlug: "scarifiers",
  },
  "leaf-blowers": {
    slug: "leaf-blowers",
    title: "Leaf Blowers",
    singular: "leaf blower",
    amazonKeywords: "leaf blower cordless garden vacuum UK",
    intro: "From quick patio clear-ups to autumn lawn rescue, a leaf blower saves hours of raking. These are the highest-rated UK leaf blowers on Amazon.",
    considerations: [
      { title: "Power source", body: "Cordless is the modern default — quiet, light and cordless freedom. Corded is cheap and constant. Petrol still wins for huge gardens." },
      { title: "Air speed (mph) vs volume (CFM/m³/h)", body: "High mph moves wet, stuck leaves. High CFM clears wide areas fast. The best blowers balance both." },
      { title: "3-in-1 (blow, vac, mulch)", body: "A mulching ratio of 10:1 or better hugely reduces collection bag emptying." },
      { title: "Weight", body: "Under 4kg is comfortable for long sessions; check for a shoulder strap on heavier units." },
    ],
    heroGradient: "bg-linear-to-br from-orange-50 to-amber-50",
    productCategorySlug: "leaf-blowers",
  },
  "log-splitters": {
    slug: "log-splitters",
    title: "Log Splitters",
    singular: "log splitter",
    amazonKeywords: "log splitter electric hydraulic UK",
    intro: "Splitting logs by hand is slow, tiring work. The right log splitter depends on how much wood you process, where you work and whether you have mains power. Below we've split the top-rated UK models into electric, petrol and manual machines so you can compare like with like.",
    sections: [
      {
        id: "electric",
        title: "Electric Log Splitters",
        intro: "Best for most homes with a woodburner. Electric hydraulic splitters run quietly from a 230V socket, need no fuel and suit seasoned logs up to roughly 25cm across. Typical home models are rated at 4–7 tonnes.",
        amazonKeywords: "electric hydraulic log splitter 230V",
        mustMatch: "electric|\\d{3,4}\\s?w\\b",
        pinnedOnly: true,
        pinnedAsins: ["B076M6QZLQ", "B00MISTMXS", "B07Z42167W", "B091J2DVZL", "B09445836C", "B0CWVG34J6"],
        reviews: {
          B076M6QZLQ: "forest-master-fm5t-tc-electric-log-splitter-review",
          B00MISTMXS: "forest-master-fm8d-tc-electric-log-splitter-review",
          B07Z42167W: "scheppach-hl810-vertical-electric-log-splitter-review",
          B091J2DVZL: "the-handy-thls-4g-electric-log-splitter-review",
          B09445836C: "scheppach-hl760ls-electric-log-splitter-review",
          B0CWVG34J6: "platinum-pls7hws-7-ton-electric-log-splitter-review",
        },
        exclude: "petrol|gas|\\d+\\s?cc|\\d+\\s?hp",
      },
      {
        id: "petrol",
        title: "Petrol Log Splitters",
        intro: "For large volumes, big hardwood rounds or sites without mains power. Petrol splitters usually offer 7–20+ tonnes and often split vertically, but they are heavier, louder and need regular engine servicing.",
        amazonKeywords: "log splitter petrol hydraulic 7 ton",
        mustMatch: "petrol|gas\\b|engine|\\d+\\s?cc|\\d+\\s?hp",
        exclude: "electric(?!\\s?start)",
        // Forest Master FM8FF-HTW (Honda), SIP 8 Ton 196cc, Lumag HB8N vertical
        pinnedOnly: true,
        pinnedAsins: ["B0F48K8BWQ", "B09S6SJ7CS", "B0DNKJ8N44", "B07YLT3BGB"],
        reviews: {
          B0F48K8BWQ: "ezasin-20-ton-petrol-log-splitter-review",
          B09S6SJ7CS: "forest-master-fm8ff-htw-petrol-kinetic-log-splitter-review",
          B0DNKJ8N44: "sip-8-ton-petrol-log-splitter-review",
          B07YLT3BGB: "lumag-hb8n-petrol-log-splitter-review",
        },
      },
      {
        id: "manual",
        title: "Manual Log Splitters",
        intro: "No power, no fuel and almost no maintenance. Foot- or hand-pump hydraulic splitters and kindling splitters suit occasional use, smaller logs and making kindling — but every split takes physical effort.",
        amazonKeywords: "manual log splitter hydraulic foot kindling",
        mustMatch: "manual|foot|hand|kindling|cracker",
        pinnedOnly: true,
        pinnedAsins: ["B01KKU8Z0Q", "B0H1DHBRM6", "B0G1CN65SN", "B0CLC9P16H"],
        reviews: {
          B01KKU8Z0Q: "kindling-cracker-original-log-splitter-review",
          B0H1DHBRM6: "met-xt-kindling-splitter-wedge-review",
          B0G1CN65SN: "forest-master-usbb-kindling-splitter-review",
          B0CLC9P16H: "vliebb-wall-mounted-kindling-splitter-review",
        },
        exclude: "electric|petrol|engine|motor|\\d{3,4}\\s?w\\b",
      },
    ],
    considerations: [
      { title: "Electric, petrol or manual?", body: "Electric suits most garden users with mains power. Petrol is for high volumes, big rounds or remote sites. Manual splitters are fine for occasional use and kindling." },
      { title: "Splitting force (tonnes)", body: "Manufacturer tonnage is a guide, not a guarantee. 4–5 tonnes generally handles seasoned softwood and smaller hardwood; knotty or green hardwood needs more force." },
      { title: "Log capacity", body: "Check the maximum log length and diameter. Many home electric models accept logs around 37–52cm long and up to about 25cm in diameter." },
      { title: "Horizontal vs vertical", body: "Horizontal splitters suit smaller logs at waist height. Vertical machines let you roll heavy rounds into place instead of lifting them." },
      { title: "Safety controls", body: "Look for two-handed operation, a sturdy log guard and a stable stand or wheels. Always wear gloves, eye protection and safety boots." },
      { title: "Maintenance", body: "Check hydraulic oil levels, bleed screws and wedge sharpness. Petrol models also need oil changes, fresh fuel and spark plug checks." },
    ],
    faqs: [
      { q: "Is an electric or petrol log splitter better?", a: "For most UK homes an electric log splitter is the better choice: quieter, cheaper and lower maintenance. Petrol makes sense for large volumes, big hardwood rounds or work away from mains power." },
      { q: "What tonnage log splitter do I need?", a: "Around 4–5 tonnes suits seasoned softwood and smaller logs; 6–8 tonnes is more comfortable for hardwood. Large, knotty or green rounds usually need a petrol splitter of 10 tonnes or more." },
      { q: "Are manual log splitters any good?", a: "Yes, for occasional use and smaller logs. Foot-pump hydraulic and kindling splitters are safe, quiet and cheap, but slow and tiring for regular, large volumes." },
    ],
    heroGradient: "bg-linear-to-br from-stone-50 to-amber-50",
    productCategorySlug: "log-splitters",
  },
  "shredders": {
    slug: "shredders",
    title: "Garden Shredders",
    singular: "garden shredder",
    amazonKeywords: "garden shredder chipper electric UK",
    intro: "Turn prunings into mulch instead of trips to the tip. Here are the highest-rated UK garden shredders on Amazon.",
    considerations: [
      { title: "Impact vs roller (silent)", body: "Impact shredders chip fast but are loud — fine for tough woody material. Roller (silent) shredders crush softer green waste at neighbour-friendly noise levels." },
      { title: "Branch capacity", body: "35–40mm covers most garden waste; 45mm+ for established shrubs and hedges." },
      { title: "Motor power", body: "2400W+ for impact, 2500W+ for quiet roller models." },
      { title: "Anti-jam reverse", body: "A lifesaver — avoid models without it." },
    ],
    heroGradient: "bg-linear-to-br from-green-50 to-lime-50",
    productCategorySlug: "shredders",
  },
  "grass-trimmers-brushcutters": {
    slug: "grass-trimmers-brushcutters",
    title: "Grass Trimmers & Strimmers",
    singular: "grass trimmer",
    amazonKeywords: "grass trimmer strimmer cordless UK",
    intro: "For neat lawn edges and clearing rough patches a mower can't reach, a strimmer is essential. Here are the top-rated UK trimmers on Amazon.",
    considerations: [
      { title: "Cordless, corded or petrol", body: "36V cordless suits most gardens. Petrol brushcutters are for very long grass or brambles." },
      { title: "Cutting width", body: "25cm is nimble; 30cm+ covers ground faster." },
      { title: "Line vs blade", body: "Bump-feed line is best for grass; metal blades cut woody stems on brushcutters." },
      { title: "Weight & shaft", body: "A telescopic shaft and shoulder harness make a huge difference for long sessions." },
    ],
    heroGradient: "bg-linear-to-br from-emerald-50 to-teal-50",
    productCategorySlug: "grass-trimmers-brushcutters",
  },
  "tillers-rotavators": {
    slug: "tillers-rotavators",
    title: "Tillers & Rotavators",
    singular: "tiller",
    amazonKeywords: "garden tiller rotavator cultivator UK",
    intro: "Preparing a vegetable bed by hand is back-breaking. An electric tiller turns hours of digging into minutes. Top UK picks below.",
    considerations: [
      { title: "Working width & depth", body: "30–35cm width, 20cm+ depth handles most veg beds and new lawn prep." },
      { title: "Electric vs petrol", body: "Electric is cleaner, quieter and ideal for soft existing soil. Petrol breaks new ground and clay." },
      { title: "Tine type", body: "Forward-rotating for tilling soft soil; counter-rotating for hard or new ground." },
      { title: "Weight", body: "Heavier units self-feed into the soil but are harder to manoeuvre on raised beds." },
    ],
    heroGradient: "bg-linear-to-br from-amber-50 to-stone-50",
    productCategorySlug: "tillers-rotavators",
  },
  "earth-augers": {
    slug: "earth-augers",
    title: "Earth Augers",
    singular: "earth auger",
    amazonKeywords: "earth auger post hole digger UK",
    intro: "For fence posts, planting trees or installing a deck, a powered earth auger digs holes in seconds. Top-rated UK models below.",
    considerations: [
      { title: "Power source", body: "Petrol is most common for fencing work; cordless models are fine for occasional garden use." },
      { title: "Auger diameter", body: "100–150mm for fence posts; 200mm+ for tree planting." },
      { title: "One-person vs two-person", body: "Two-handed designs offer more control on hard ground." },
      { title: "Safety", body: "Look for an anti-kickback clutch — augers can spin you hard if they hit a root." },
    ],
    heroGradient: "bg-linear-to-br from-stone-50 to-zinc-50",
    productCategorySlug: "earth-augers",
  },
  "water-pumps": {
    slug: "water-pumps",
    title: "Water Pumps",
    singular: "water pump",
    amazonKeywords: "garden water pump submersible UK",
    intro: "From draining a flooded lawn to topping up a pond or watering an allotment, a good water pump pays for itself fast. Top UK picks below.",
    considerations: [
      { title: "Clean vs dirty water", body: "Dirty-water pumps pass solids up to 35mm; clean-water pumps move clear water faster but clog easily." },
      { title: "Flow rate (litres/hour)", body: "10,000+ L/h for fast drainage; 5,000 L/h is plenty for irrigation." },
      { title: "Max head height", body: "How high the pump can push water vertically — match to your garden's slope." },
      { title: "Auto float switch", body: "Turns the pump off when water drops below a level — essential for unattended pumping." },
    ],
    heroGradient: "bg-linear-to-br from-cyan-50 to-blue-50",
    productCategorySlug: "water-pumps",
  },
  "air-compressors": {
    slug: "air-compressors",
    title: "Air Compressors",
    singular: "air compressor",
    amazonKeywords: "air compressor portable UK",
    intro: "From inflating tyres to running nail guns and spray equipment, a compressor is one of the most useful tools in any workshop. Top UK picks below.",
    considerations: [
      { title: "Tank size (litres)", body: "6L for nailers and tyres; 24L+ for spray and continuous-use tools." },
      { title: "CFM / L/min air delivery", body: "Match the tool's air consumption — undersized compressors cycle constantly." },
      { title: "Oil-free vs oil-lubricated", body: "Oil-free is low-maintenance; oil-lubricated lasts longer under heavy use." },
      { title: "Noise", body: "Silent compressors run at 60–70dB vs 90dB+ for standard models." },
    ],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
    productCategorySlug: "air-compressors",
  },
  "cement-mixers": {
    slug: "cement-mixers",
    title: "Cement Mixers",
    singular: "cement mixer",
    amazonKeywords: "cement mixer concrete UK",
    intro: "From small landscaping jobs to building a wall, a cement mixer saves your back and gives a much better consistency than hand-mixing. Top UK picks below.",
    considerations: [
      { title: "Drum capacity (litres)", body: "120–140L is the standard DIY size; 160L+ for larger builds." },
      { title: "Motor (watts)", body: "550–650W for occasional DIY; 1100W+ for daily site use." },
      { title: "Frame & wheels", body: "Pneumatic wheels make moving a full mixer far easier than solid plastic ones." },
      { title: "Tilt mechanism", body: "A smooth handwheel tip is essential for clean pours." },
    ],
    heroGradient: "bg-linear-to-br from-gray-50 to-stone-50",
    productCategorySlug: "cement-mixers",
  },
  "welders": {
    slug: "welders",
    title: "Welders",
    singular: "welder",
    amazonKeywords: "MIG welder TIG welder inverter UK",
    intro: "Whether you're a hobbyist or running a workshop, modern inverter welders are lighter, cheaper and more capable than ever. Top-rated UK picks below.",
    considerations: [
      { title: "MIG, TIG or stick", body: "MIG is the easiest to learn; TIG gives the cleanest welds; stick is best outdoors." },
      { title: "Amperage range", body: "140A handles most home/farm jobs; 200A+ for thicker steel." },
      { title: "Duty cycle", body: "60% at rated amps means 6 minutes welding in every 10 — important for production work." },
      { title: "Gas vs gasless", body: "Gas MIG gives cleaner welds; gasless flux-core is more portable." },
    ],
    heroGradient: "bg-linear-to-br from-orange-50 to-red-50",
    productCategorySlug: "welders",
  },
  "drills": {
    slug: "drills",
    title: "Drills",
    singular: "drill",
    amazonKeywords: "cordless drill combi drill UK",
    intro: "A good cordless drill is the single most useful tool in any home. Here are the highest-rated UK drills on Amazon.",
    considerations: [
      { title: "Voltage (V)", body: "12V is light for around-the-house jobs; 18V/20V is the all-round sweet spot; 36V+ for heavy masonry." },
      { title: "Brushless motor", body: "More power, longer runtime, longer life — well worth the small premium." },
      { title: "Combi vs impact driver", body: "A combi drills, screws and hammer-drills masonry. An impact driver excels at long screws." },
      { title: "Battery system", body: "Stick to one ecosystem (Makita LXT, DeWalt 18V, Bosch ProCore etc.) so batteries swap between tools." },
    ],
    heroGradient: "bg-linear-to-br from-red-50 to-rose-50",
    productCategorySlug: "drills",
  },
  "grinders": {
    slug: "grinders",
    title: "Angle Grinders",
    singular: "angle grinder",
    amazonKeywords: "angle grinder 115mm 125mm UK",
    intro: "Cutting paving slabs, sharpening mower blades, removing rust — an angle grinder does it all. Top-rated UK picks below.",
    considerations: [
      { title: "Disc size", body: "115mm is light and nimble; 125mm offers more cut depth; 230mm for heavy demolition." },
      { title: "Motor power", body: "750W+ for 115mm, 1100W+ for 125mm, 2000W+ for 230mm." },
      { title: "Cordless vs corded", body: "18V cordless is brilliant for site/garden; corded is unbeatable on continuous use." },
      { title: "Safety", body: "Soft-start, anti-kickback and a quick-release guard are non-negotiable for safe use." },
    ],
    heroGradient: "bg-linear-to-br from-zinc-50 to-slate-50",
    productCategorySlug: "grinders",
  },
  "saws": {
    slug: "saws",
    title: "Power Saws",
    singular: "power saw",
    amazonKeywords: "circular saw mitre saw jigsaw UK",
    intro: "From precise mitre cuts to rough framing, the right saw transforms your DIY. Top-rated UK power saws below.",
    considerations: [
      { title: "Saw type", body: "Circular for sheet goods; mitre for trim and framing; jigsaw for curves; reciprocating for demolition." },
      { title: "Blade size & cut depth", body: "Match to the thickest material you cut regularly." },
      { title: "Cordless vs corded", body: "18V cordless is now genuinely powerful enough for most home use." },
      { title: "Dust extraction", body: "A vac port keeps the cut line visible and your workshop tidy." },
    ],
    heroGradient: "bg-linear-to-br from-amber-50 to-orange-50",
    productCategorySlug: "saws",
  },
  "compactor-plates": {
    slug: "compactor-plates",
    title: "Compactor Plates",
    singular: "compactor plate",
    amazonKeywords: "wacker plate compactor petrol UK",
    intro: "Laying a patio, drive or shed base? A wacker plate gives you a properly compacted, level sub-base. Top-rated UK picks below.",
    considerations: [
      { title: "Centrifugal force (kN)", body: "10–15kN suits patios; 18kN+ for driveways and large sub-bases." },
      { title: "Plate size", body: "Larger plates cover ground faster but are harder to manoeuvre in tight spots." },
      { title: "Engine", body: "Honda GX engines are the gold standard for reliability." },
      { title: "Water tank", body: "Useful for tarmac and block paving with kiln-dried sand." },
    ],
    heroGradient: "bg-linear-to-br from-stone-50 to-neutral-50",
    productCategorySlug: "compactor-plates",
  },
  "mini-dumpers": {
    slug: "mini-dumpers",
    title: "Mini Dumpers & Powered Wheelbarrows",
    singular: "mini dumper",
    amazonKeywords: "powered wheelbarrow mini dumper UK",
    intro: "Moving tonnes of soil, gravel or muck by wheelbarrow is brutal. A powered dumper makes light work of it. Top-rated UK picks below.",
    considerations: [
      { title: "Load capacity (kg)", body: "150–200kg for garden use; 300kg+ for landscaping and building work." },
      { title: "Tracked vs wheeled", body: "Tracks excel on soft, sloped or muddy ground; wheels are faster on hard surfaces." },
      { title: "Tipping mechanism", body: "Hydraulic tip is much easier than manual on a heavy load." },
      { title: "Petrol vs electric", body: "Petrol still dominates for power; cordless is catching up for light garden use." },
    ],
    heroGradient: "bg-linear-to-br from-yellow-50 to-stone-50",
    productCategorySlug: "mini-dumpers",
  },
  "vacuum-cleaners": {
    slug: "vacuum-cleaners",
    title: "Vacuum Cleaners",
    singular: "vacuum cleaner",
    amazonKeywords: "cordless vacuum cleaner UK",
    intro: "From cordless sticks to powerful uprights and clever robot vacs, here are the highest-rated UK vacuum cleaners on Amazon.",
    considerations: [
      { title: "Type", body: "Cordless stick for everyday quick clean; upright for big carpeted homes; cylinder for stairs and cars; robot for hands-off maintenance." },
      { title: "Suction & battery", body: "On cordless models, look for 200AW+ suction and 40+ minute runtime." },
      { title: "Filtration", body: "HEPA filtration matters for allergy/asthma sufferers." },
      { title: "Bagged vs bagless", body: "Bagless is cheaper to run; bagged is cleaner to empty for allergy sufferers." },
    ],
    heroGradient: "bg-linear-to-br from-indigo-50 to-violet-50",
    productCategorySlug: "vacuum-cleaners",
  },
  "seeds": {
    slug: "seeds",
    title: "Seeds",
    singular: "seed packet",
    amazonKeywords: "vegetable flower seeds UK",
    intro: "The right seeds turn a small budget into a season's worth of veg, herbs and flowers. We've gathered the best-rated UK seed packets and starter collections on Amazon — refreshed automatically.",
    considerations: [
      { title: "Sowing season", body: "Check the packet for sowing months. Most UK veg seeds go in March–May; hardy annuals can be sown autumn or spring." },
      { title: "Variety vs collection packs", body: "Single-variety packs let you choose proven UK favourites. Collection boxes are great value if you're starting from scratch." },
      { title: "Germination & freshness", body: "Look for clearly printed packed-by/sow-by dates. Fresh seed germinates far better than old stock." },
      { title: "Organic & heirloom", body: "Organic and heirloom (open-pollinated) seeds let you save your own seed for next year — modern F1 hybrids do not." },
    ],
    faqs: [
      { q: "When should I sow seeds in the UK?", a: "Most tender veg (tomatoes, courgettes) is sown indoors March–April. Hardy crops (carrots, peas, lettuce) go direct outside April–June. Always check the packet." },
      { q: "How long do seeds last?", a: "Most veg and flower seeds keep 2–4 years in a cool, dry, dark place. Parsnips and alliums are best used fresh each year." },
    ],
    heroGradient: "bg-linear-to-br from-green-50 to-emerald-50",
    productCategorySlug: "seeds",
  },
  "vegetables": {
    slug: "vegetables",
    title: "Vegetable Plants & Growing Kits",
    singular: "vegetable plant",
    amazonKeywords: "vegetable plants seed potatoes onion sets UK",
    intro: "Skip the tricky germination stage with ready-to-plant vegetables, seed potatoes, onion sets and grow-your-own kits. Top-rated UK picks below — refreshed automatically.",
    considerations: [
      { title: "Plug plants vs sets vs seed", body: "Plug plants are the fastest route to harvest. Seed potatoes and onion sets are reliable and cheap. Seed gives the widest variety." },
      { title: "Variety choice", body: "Look for AGM (RHS Award of Garden Merit) varieties and ones bred for UK conditions — they crop better in our cooler, wetter summers." },
      { title: "Planting window", body: "Most plug plants ship March–June. Seed potatoes Jan–March, garlic and overwintering onions in autumn." },
      { title: "Container vs ground", body: "If you only have a patio, choose patio-friendly varieties (bush tomatoes, dwarf French beans, salad leaves)." },
    ],
    faqs: [
      { q: "What are the easiest vegetables for UK beginners?", a: "Courgettes, runner beans, salad leaves, radishes, potatoes and onions are forgiving and crop reliably almost anywhere in the UK." },
      { q: "Do I need a greenhouse?", a: "No — most UK vegetables crop fine outdoors. A cold frame or polytunnel just extends the season at both ends." },
    ],
    heroGradient: "bg-linear-to-br from-green-50 to-lime-50",
    productCategorySlug: "vegetables",
  },
  "fruits": {
    slug: "fruits",
    title: "Fruit Trees, Bushes & Plants",
    singular: "fruit plant",
    amazonKeywords: "fruit trees strawberry plants raspberry canes UK",
    intro: "Plant once, harvest for decades. From patio apple trees to strawberry runners and raspberry canes, here are the best-rated UK fruit plants on Amazon.",
    considerations: [
      { title: "Rootstock & size", body: "On apples and pears, the rootstock controls the final size. M27 / M9 for patio pots, M26 / MM106 for normal gardens, MM111 for orchards." },
      { title: "Self-fertile vs pollination partners", body: "Self-fertile varieties (most cherries, raspberries, blueberries) crop alone. Many apples and pears need a pollination partner nearby." },
      { title: "Bare-root vs potted", body: "Bare-root (Nov–March) is cheapest and establishes well. Potted plants can go in any time the ground isn't frozen." },
      { title: "Soil & site", body: "Most soft fruit needs sun and free-draining soil. Blueberries are the exception — they need ericaceous (acidic) compost." },
    ],
    faqs: [
      { q: "How long until fruit trees crop?", a: "Patio fruit trees often crop the year after planting. Standard apple, pear and plum trees usually crop in years 3–4." },
      { q: "Can I grow fruit in pots?", a: "Yes — strawberries, blueberries, patio apples and dwarf cherries all crop heavily in containers with regular feeding." },
    ],
    heroGradient: "bg-linear-to-br from-red-50 to-orange-50",
    productCategorySlug: "fruits",
  },
  "garden-plants": {
    slug: "garden-plants",
    title: "Garden Plants",
    singular: "garden plant",
    amazonKeywords: "garden plants perennials shrubs UK",
    intro: "Build a garden that looks great in every season with the right mix of perennials, shrubs and bedding. Best-rated UK garden plants on Amazon below.",
    considerations: [
      { title: "Perennials vs annuals", body: "Perennials come back every year and are best value long-term. Annuals (bedding) give intense colour fast but need replacing each year." },
      { title: "Sun, shade or part shade", body: "Match the plant to your spot — most labels list aspect clearly. Lavender and roses want full sun; ferns and hostas thrive in shade." },
      { title: "Hardiness", body: "RHS H4 / H5 ratings cope with normal UK winters. H3 and below need a sheltered spot or winter protection." },
      { title: "Pot size on arrival", body: "9cm plug plants are cheapest but slow to establish. 1L–3L potted plants give an instant effect." },
    ],
    faqs: [
      { q: "When is the best time to plant?", a: "Autumn (Sept–Nov) is ideal — soil is warm, plants establish before summer drought. Spring is the next best window." },
      { q: "What plants give year-round interest?", a: "Mix evergreen shrubs (skimmia, choisya), spring bulbs, summer perennials (geranium, salvia) and autumn grasses for 12-month colour." },
    ],
    heroGradient: "bg-linear-to-br from-green-50 to-teal-50",
    productCategorySlug: "garden-plants",
  },
  "compost": {
    slug: "compost",
    title: "Compost & Soil Improvers",
    singular: "compost",
    amazonKeywords: "compost peat free multipurpose UK",
    intro: "Good compost is the foundation of a productive garden. Here are the best-rated UK peat-free composts, soil improvers and specialist mixes on Amazon.",
    considerations: [
      { title: "Peat-free", body: "Peat extraction destroys carbon-rich habitats. Modern peat-free mixes (coir, wood fibre, bark) now match or beat peat-based compost — and the UK ban is rolling out." },
      { title: "Multipurpose vs specialist", body: "Multipurpose is fine for most pots and beds. Use ericaceous for blueberries / camellias, John Innes for long-term containers, seed compost for sowing." },
      { title: "Bag size & weight", body: "Big 60–75L bags are cheaper per litre but heavy to handle. Compressed bales rehydrate to a much larger volume — great if storage is tight." },
      { title: "Quality marks", body: "Look for the RHS endorsement or 'Approved' logos from independent UK compost trials." },
    ],
    faqs: [
      { q: "How much compost do I need?", a: "A 60L bag covers about 1 m² to 6cm depth. For a new raised bed (1.2m × 2.4m × 30cm) plan on around 14 × 60L bags." },
      { q: "Can I use compost as mulch?", a: "Yes — a 5cm layer of well-rotted compost or soil improver around plants suppresses weeds, retains moisture and feeds the soil." },
    ],
    heroGradient: "bg-linear-to-br from-amber-50 to-stone-50",
    productCategorySlug: "compost",
  },
  "space-heaters": {
    slug: "space-heaters",
    title: "Space Heaters",
    singular: "space heater",
    amazonKeywords: "space heater electric propane UK",
    intro: "Warm up workshops, greenhouses and spare rooms without heating the whole house. Here are the highest-rated UK space heaters on Amazon.",
    considerations: [
      { title: "Power source", body: "Electric fan and oil-filled radiators plug in anywhere. Propane and kerosene heaters output serious BTUs but need ventilation. Infrared panels are silent and wall-mountable." },
      { title: "Heat output (kW / BTU)", body: "1–2kW (3,400–6,800 BTU) warms a small room. 3kW+ (10,000+ BTU) heats workshops and large conservatories." },
      { title: "Safety features", body: "Tip-over cut-off, overheat protection and oxygen depletion sensors (for fuel-burning models) are essential." },
      { title: "Running costs", body: "Electric is expensive per kWh but 100% efficient at point of use. Propane is cheaper per unit but needs cylinder swaps." },
    ],
    heroGradient: "bg-linear-to-br from-orange-50 to-red-50",
    productCategorySlug: "space-heaters",
  },
};
